// apps/web/src/components/tutorials/AdaptiveGuideVideoPlayer.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTheme } from 'next-themes';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize2,
  Minimize2,
  RotateCcw,
  Sun,
  Moon,
  Sparkles,
  Video,
  Clock,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TutorialModule } from '@/data/howToTutorials';

interface AdaptiveGuideVideoPlayerProps {
  module: TutorialModule;
  activeTimestamp?: string;
  onStepClick?: (timestamp: string) => void;
}

export const AdaptiveGuideVideoPlayer: React.FC<AdaptiveGuideVideoPlayerProps> = ({
  module,
  activeTimestamp,
  onStepClick,
}) => {
  const { resolvedTheme } = useTheme();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Allow manual override or auto-follow theme
  const [manualTheme, setManualTheme] = useState<'auto' | 'light' | 'dark'>('auto');
  const activeVideoTheme = manualTheme === 'auto' ? (resolvedTheme === 'dark' ? 'dark' : 'light') : manualTheme;

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [scrubTime, setScrubTime] = useState<number | null>(null);
  const [isSeeking, setIsSeeking] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSeekingRef = useRef(false);
  const wasPlayingBeforeSeekRef = useRef(false);

  // Get active video source with trimmed URLs
  const currentVideoConfig = module.videos[activeVideoTheme];
  const rawUrl = currentVideoConfig?.url ? currentVideoConfig.url.trim() : '';
  const hasRealVideo = Boolean(rawUrl.length > 0);
  const videoSrc = hasRealVideo ? rawUrl : '';
  const posterSrc = currentVideoConfig?.poster ? currentVideoConfig.poster.trim() : '';

  // Convert "M:SS" string to seconds
  const parseTimestamp = useCallback((ts?: string): number => {
    if (!ts) return 0;
    const parts = ts.split(':').map(Number);
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    return 0;
  }, []);

  // Format seconds to "M:SS"
  const formatTime = (seconds: number): string => {
    if (isNaN(seconds) || !isFinite(seconds) || seconds < 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  // Safe seek handler that clamps within bounds and catches browser aborts
  const safeSeek = useCallback((targetSec: number) => {
    const video = videoRef.current;
    if (!video) return;
    if (!isFinite(targetSec) || isNaN(targetSec)) return;

    const maxDuration = isFinite(video.duration) && video.duration > 0
      ? video.duration
      : (duration > 0 ? duration : parseTimestamp(module.duration) || 300);

    const clamped = Math.max(0, Math.min(targetSec, maxDuration));

    try {
      if ('fastSeek' in video && typeof (video as any).fastSeek === 'function') {
        try {
          (video as any).fastSeek(clamped);
        } catch {
          video.currentTime = clamped;
        }
      } else {
        video.currentTime = clamped;
      }
      setCurrentTime(clamped);
    } catch (err) {
      console.warn('Seek execution warning:', err);
    }
  }, [duration, module.duration, parseTimestamp]);

  // Scrubbing handlers (dragging on the slider)
  // onValueChange updates the local preview time smoothly without repeatedly querying the media network
  const handleScrubChange = (value: number[]) => {
    if (value[0] === undefined) return;
    if (!isSeekingRef.current) {
      isSeekingRef.current = true;
      setIsSeeking(true);
      wasPlayingBeforeSeekRef.current = isPlaying;
      if (videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause();
      }
    }
    setScrubTime(value[0]);
  };

  // onValueCommit commits the actual seek once the user releases the slider or clicks a point
  const handleScrubCommit = (value: number[]) => {
    if (value[0] === undefined) return;
    const targetSec = value[0];

    isSeekingRef.current = false;
    setIsSeeking(false);
    setScrubTime(null);

    safeSeek(targetSec);

    // If it was playing prior to scrub, resume playback after seek
    if (wasPlayingBeforeSeekRef.current && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  };

  // Preserve time when switching between light and dark
  const handleThemeSwitch = (targetTheme: 'auto' | 'light' | 'dark') => {
    const savedTime = videoRef.current ? videoRef.current.currentTime : currentTime;
    const wasPlaying = isPlaying;
    setManualTheme(targetTheme);
    setHasError(false);

    setTimeout(() => {
      if (videoRef.current) {
        safeSeek(savedTime);
        if (wasPlaying) {
          videoRef.current.play().catch(() => {});
        }
      }
    }, 120);
  };

  // Seek when activeTimestamp prop changes externally
  useEffect(() => {
    if (activeTimestamp && videoRef.current) {
      const targetSec = parseTimestamp(activeTimestamp);
      safeSeek(targetSec);
      if (videoRef.current.paused) {
        videoRef.current.play().catch(() => {});
      }
    }
  }, [activeTimestamp, parseTimestamp, safeSeek]);

  // Video element event listeners
  const onTimeUpdate = () => {
    if (videoRef.current && !isSeekingRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const onLoadedMetadata = () => {
    if (videoRef.current) {
      const vidDur = videoRef.current.duration;
      if (isFinite(vidDur) && vidDur > 0) {
        setDuration(vidDur);
      }
      setHasError(false);
      setErrorMessage(null);
    }
  };

  const handleVideoError = () => {
    const video = videoRef.current;
    const mediaError = video?.error;

    // Code 1 is MEDIA_ERR_ABORTED - common when seeking over range requests.
    // Do NOT treat this as a fatal player crash!
    if (mediaError && mediaError.code === 1) {
      console.warn('Transient seek abort detected, ignoring.');
      return;
    }

    // Only surface fatal errors if not in the middle of seeking
    if (!isSeekingRef.current && mediaError && mediaError.code === 4) {
      setHasError(true);
      setErrorMessage('Unable to load video format or source.');
    }
  };

  const handleReloadVideo = () => {
    setHasError(false);
    setErrorMessage(null);
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    const nextMute = !isMuted;
    videoRef.current.muted = nextMute;
    setIsMuted(nextMute);
  };

  const handleVolumeChange = (value: number[]) => {
    if (!videoRef.current || value[0] === undefined) return;
    const nextVol = Math.max(0, Math.min(1, value[0]));
    videoRef.current.volume = nextVol;
    setVolume(nextVol);
    setIsMuted(nextVol === 0);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  const cycleSpeed = () => {
    if (!videoRef.current) return;
    const speeds = [1, 1.25, 1.5, 2];
    const nextIndex = (speeds.indexOf(playbackRate) + 1) % speeds.length;
    const nextSpeed = speeds[nextIndex];
    videoRef.current.playbackRate = nextSpeed;
    setPlaybackRate(nextSpeed);
  };

  const restartVideo = () => {
    if (!videoRef.current) return;
    safeSeek(0);
    videoRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
  };

  // Auto-hide controls on mouse idle
  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying && !isSeekingRef.current) setShowControls(false);
    }, 2800);
  }, [isPlaying]);

  // Current display value on the slider and counter
  const displayTime = isSeeking && scrubTime !== null ? scrubTime : currentTime;
  const effectiveDuration = duration > 0 ? duration : parseTimestamp(module.duration) || 100;

  return (
    <div className="relative group">
      {/* Ambient Glow Aura */}
      <div className="absolute -inset-1.5 bg-gradient-to-r from-primary/30 via-indigo-500/20 to-primary/30 rounded-[2.5rem] blur-2xl opacity-50 group-hover:opacity-80 transition duration-700 pointer-events-none" />

      {/* Main Cinema Device Frame */}
      <div
        ref={containerRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => isPlaying && !isSeeking && setShowControls(false)}
        className="relative w-full aspect-video rounded-[2rem] overflow-hidden bg-slate-950 border border-white/15 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] select-none flex flex-col justify-between"
      >
        {/* Cinema Window Top Bar */}
        <div
          className={`px-5 py-3.5 bg-black/60 backdrop-blur-xl border-b border-white/10 flex items-center justify-between z-30 transition-opacity duration-300 ${
            showControls || !isPlaying || isSeeking ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          {/* Mac OS Window Dots */}
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-500/80 inline-block" />
            <span className="w-3 h-3 rounded-full bg-yellow-500/80 inline-block" />
            <span className="w-3 h-3 rounded-full bg-green-500/80 inline-block" />
            <span className="ml-3 text-xs font-semibold text-white/80 tracking-wide hidden sm:inline truncate max-w-[280px]">
              {module.title}
            </span>
          </div>

          {/* Theme Adaptation Switcher Pill */}
          <div className="flex items-center gap-1 bg-white/10 backdrop-blur-md p-1 rounded-full border border-white/15">
            <button
              onClick={() => handleThemeSwitch('auto')}
              className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all ${
                manualTheme === 'auto'
                  ? 'bg-primary text-black shadow-md font-bold'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
              title="Auto-follow site theme"
            >
              <Sparkles className="w-3 h-3" />
              <span>Auto</span>
            </button>

            <button
              onClick={() => handleThemeSwitch('light')}
              className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all ${
                activeVideoTheme === 'light' && manualTheme !== 'auto'
                  ? 'bg-amber-400 text-black shadow-md font-bold'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
              title="Preview in Light Mode"
            >
              <Sun className="w-3 h-3" />
              <span className="hidden sm:inline">Light</span>
            </button>

            <button
              onClick={() => handleThemeSwitch('dark')}
              className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all ${
                activeVideoTheme === 'dark' && manualTheme !== 'auto'
                  ? 'bg-indigo-600 text-white shadow-md font-bold'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
              }`}
              title="Preview in Dark Mode"
            >
              <Moon className="w-3 h-3" />
              <span className="hidden sm:inline">Dark</span>
            </button>
          </div>
        </div>

        {/* Video Canvas OR Rich Themed Mockup Screen */}
        <div className="relative flex-1 w-full overflow-hidden flex items-center justify-center">
          {videoSrc && !hasError ? (
            <>
              <video
                ref={videoRef}
                src={videoSrc}
                poster={posterSrc}
                preload="metadata"
                className="w-full h-full object-cover cursor-pointer"
                onClick={togglePlay}
                onTimeUpdate={onTimeUpdate}
                onLoadedMetadata={onLoadedMetadata}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onSeeking={() => setIsBuffering(true)}
                onSeeked={() => setIsBuffering(false)}
                onWaiting={() => setIsBuffering(true)}
                onPlaying={() => setIsBuffering(false)}
                onCanPlay={() => setIsBuffering(false)}
                onEnded={() => setIsPlaying(false)}
                onError={handleVideoError}
                playsInline
                crossOrigin="anonymous"
              />

              {/* Buffering Spinner */}
              {isBuffering && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/30 backdrop-blur-xs">
                  <div className="p-3.5 rounded-full bg-black/70 border border-white/20 text-primary shadow-xl">
                    <Loader2 className="w-8 h-8 animate-spin" />
                  </div>
                </div>
              )}
            </>
          ) : hasError ? (
            /* Error Fallback with Reload Button */
            <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center bg-slate-950 text-white space-y-4">
              <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center text-red-400">
                <Video className="w-7 h-7" />
              </div>
              <div className="space-y-1">
                <h4 className="text-lg font-bold">Video Playback Encountered an Issue</h4>
                <p className="text-xs text-white/60 max-w-sm">
                  {errorMessage || 'The video stream was interrupted. Click below to reload.'}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="gap-2 rounded-xl border-white/20 hover:bg-white/10"
                onClick={handleReloadVideo}
              >
                <RefreshCw className="w-4 h-4" />
                Reload Video
              </Button>
            </div>
          ) : (
            /* High-Fidelity Themed App Studio Simulation */
            <div
              className={`w-full h-full flex flex-col justify-between p-6 sm:p-10 transition-colors duration-700 relative overflow-hidden ${
                activeVideoTheme === 'dark'
                  ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950/80 text-white'
                  : 'bg-gradient-to-br from-slate-50 via-sky-50/80 to-amber-50/70 text-slate-900'
              }`}
            >
              {/* Subtle Tech Grid Background */}
              <div className="absolute inset-0 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:28px_28px] opacity-15 pointer-events-none" />

              {/* Upper Header Info */}
              <div className="flex items-center justify-between relative z-10">
                <Badge
                  variant="outline"
                  className={`text-xs px-3 py-1 font-semibold flex items-center gap-1.5 backdrop-blur-md ${
                    activeVideoTheme === 'dark'
                      ? 'border-indigo-500/30 bg-indigo-500/10 text-indigo-300'
                      : 'border-amber-500/40 bg-amber-500/10 text-amber-800'
                  }`}
                >
                  {activeVideoTheme === 'dark' ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
                  <span>{activeVideoTheme === 'dark' ? 'Dark Mode Video Feed' : 'Light Mode Video Feed'}</span>
                </Badge>

                <div className="flex items-center gap-2 text-xs font-mono opacity-60">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Duration: {module.duration}</span>
                </div>
              </div>

              {/* Center Play Button & Title */}
              <div className="flex flex-col items-center justify-center text-center my-auto relative z-10 py-4">
                <div
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-primary text-black flex items-center justify-center shadow-[0_0_50px_rgba(59,130,246,0.6)] cursor-pointer hover:scale-110 active:scale-95 transition-all mb-5 group/btn"
                >
                  <Play className="w-10 h-10 sm:w-11 sm:h-11 fill-current ml-1 group-hover/btn:scale-110 transition-transform" />
                </div>

                <h3 className="text-xl sm:text-3xl font-black tracking-tight mb-2 max-w-xl">
                  {module.title}
                </h3>
                <p className="text-xs sm:text-sm max-w-lg opacity-75 leading-relaxed mx-auto">
                  {module.description}
                </p>

                {/* Cloudinary Ready Hook Indicator */}
                <div className="mt-5 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-mono bg-black/40 border border-white/10 text-white/90 backdrop-blur-md">
                  <Video className="w-3.5 h-3.5 text-primary" />
                  <span>Cloudinary ID: <strong className="text-primary font-bold">{currentVideoConfig.publicId}</strong></span>
                </div>
              </div>

              {/* Bottom Quick-Step Timeline Preview */}
              <div className="relative z-10 flex items-center justify-center gap-2 overflow-x-auto pt-2 no-scrollbar">
                {module.steps.map((step, idx) => (
                  <button
                    key={idx}
                    onClick={() => onStepClick?.(step.timestamp || '0:00')}
                    className={`text-xs px-3 py-1.5 rounded-xl whitespace-nowrap transition-all border flex items-center gap-1.5 ${
                      activeVideoTheme === 'dark'
                        ? 'bg-white/5 border-white/10 text-white/80 hover:bg-white/15'
                        : 'bg-black/5 border-black/10 text-black/80 hover:bg-black/10'
                    }`}
                  >
                    <span className="font-mono font-bold text-primary">{step.timestamp || `0:${idx * 25}`}</span>
                    <span className="font-medium truncate max-w-[120px]">{step.title}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Center Play Button on hover when real video loaded */}
          {videoSrc && !hasError && (
            <div
              onClick={togglePlay}
              className={`absolute inset-0 flex items-center justify-center cursor-pointer transition-opacity duration-300 ${
                !isPlaying || showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              <div className="w-18 h-18 rounded-full bg-black/60 backdrop-blur-md border border-white/20 text-white flex items-center justify-center hover:scale-110 transition-transform shadow-2xl">
                {isPlaying ? (
                  <Pause className="w-8 h-8 fill-current" />
                ) : (
                  <Play className="w-8 h-8 fill-current ml-1 text-primary" />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Video Control Bar Overlay */}
        <div
          className={`w-full bg-gradient-to-t from-black/95 via-black/70 to-transparent p-4 sm:p-5 pt-8 transition-opacity duration-300 z-30 ${
            showControls || !isPlaying || isSeeking ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          {/* Progress Slider with smooth scrub & commit */}
          <div className="mb-3 px-1">
            <Slider
              value={[displayTime]}
              max={effectiveDuration}
              step={0.1}
              onValueChange={handleScrubChange}
              onValueCommit={handleScrubCommit}
              className="cursor-pointer"
            />
          </div>

          {/* Action Controls Row */}
          <div className="flex items-center justify-between text-white text-sm">
            {/* Left Controls */}
            <div className="flex items-center gap-3">
              <button
                onClick={togglePlay}
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                title={isPlaying ? 'Pause' : 'Play'}
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 fill-current text-primary" />}
              </button>

              <button
                onClick={restartVideo}
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                title="Restart"
              >
                <RotateCcw className="w-4 h-4 text-white/80" />
              </button>

              {/* Volume */}
              <div className="flex items-center gap-2 group/vol">
                <button
                  onClick={toggleMute}
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                  title={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted || volume === 0 ? (
                    <VolumeX className="w-5 h-5 text-red-400" />
                  ) : (
                    <Volume2 className="w-5 h-5" />
                  )}
                </button>
                <div className="w-16 hidden sm:block">
                  <Slider
                    value={[isMuted ? 0 : volume]}
                    max={1}
                    step={0.05}
                    onValueChange={handleVolumeChange}
                    className="cursor-pointer"
                  />
                </div>
              </div>

              {/* Time Display */}
              <div className="text-xs text-white/70 font-mono ml-2">
                <span>{formatTime(displayTime)}</span>
                <span className="mx-1 text-white/40">/</span>
                <span>{formatTime(effectiveDuration)}</span>
              </div>
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={cycleSpeed}
                className="px-2.5 py-1 rounded-md text-xs font-mono font-bold bg-white/10 hover:bg-white/20 transition-colors text-white/90"
                title="Playback speed"
              >
                {playbackRate}x
              </button>

              <button
                onClick={toggleFullscreen}
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                title="Toggle Fullscreen"
              >
                {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdaptiveGuideVideoPlayer;
